const REASONS = [
  {
    icon: 'ri-medal-line',
    title: 'Experienced, Compassionate Caregivers',
    desc: 'Our caregivers are chosen for skill AND heart — each one trained in senior-specific care techniques.',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Flexible Scheduling Options',
    desc: 'From a few hours weekly to full-time or 24/7 live-in care — your schedule, your choice.',
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Fully Customized Care Plans',
    desc: 'Every senior is unique. We build care plans around the individual, not a template.',
  },
  {
    icon: 'ri-map-pin-2-line',
    title: 'Locally Rooted Team',
    desc: 'Based in Oakland, IL — part of the community and responding quickly when families need us.',
  },
  {
    icon: 'ri-heart-3-line',
    title: 'Dignity & Independence First',
    desc: 'We empower seniors to maintain control and autonomy while receiving the support they deserve.',
  },
  {
    icon: 'ri-family-line',
    title: 'Family-Centered Communication',
    desc: 'We keep families informed, involved, and at peace through every stage of the care journey.',
  },
];

const IMAGES = [
  'https://public.readdy.ai/ai/img_res/c7a0a01a85cfd74f813d6e9f9a9ae03f.jpg',
  'https://readdy.ai/api/search-image?query=elderly%20woman%20laughing%20with%20professional%20female%20caregiver%20at%20kitchen%20table%20morning%20coffee%20cups%20warm%20home%20genuine%20emotional%20connection%20senior%20companion%20care%20soft%20cream%20tones&width=400&height=320&seq=why2v3&orientation=landscape',
  'https://readdy.ai/api/search-image?query=gentle%20caregiver%20reading%20book%20with%20elderly%20woman%20in%20comfortable%20armchair%20soft%20afternoon%20light%20cozy%20warm%20home%20senior%20companion%20care%20cream%20tones&width=400&height=320&seq=why3v3&orientation=landscape',
  'https://readdy.ai/api/search-image?query=professional%20healthcare%20caregiver%20taking%20notes%20with%20senior%20couple%20at%20home%20living%20room%20warm%20attentive%20care%20planning%20family%20trust%20tones&width=400&height=420&seq=why4v3&orientation=portrait',
];

const SCREENING_STEPS = [
  { icon: 'ri-search-line', title: 'Thorough Background Check', desc: 'Criminal, sex offender, and reference screening on every hire' },
  { icon: 'ri-first-aid-kit-line', title: 'Certified & Trained', desc: 'Senior care training, CPR & first aid certified before starting' },
  { icon: 'ri-user-voice-line', title: 'In-Person Interview', desc: 'Rigorous behavioral screening and compatibility interview' },
  { icon: 'ri-refresh-line', title: 'Ongoing Monitoring', desc: 'Regular performance reviews, client feedback, and check-ins' },
];

export default function WhyChooseUs() {
  return (
    <>
      {/* Main Why Choose Us */}
      <section className="py-28 bg-offWhite">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left content */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-block w-10 h-px bg-gold"></span>
                <p className="text-gold text-xs font-bold uppercase tracking-[0.18em]">Our Difference</p>
              </div>
              <h2 className="font-heading text-navy font-bold mb-5 leading-tight" style={{ fontSize: 'clamp(1.9rem, 3.5vw, 3.2rem)' }}>
                Why Families Trust<br />Oak Senior Care
              </h2>
              <p className="text-gray-500 text-base leading-relaxed mb-10 max-w-lg">
                Since opening, we&apos;ve built our reputation on one principle: treat every senior like family. That commitment shows in everything we do — from caregiver selection to ongoing communication.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {REASONS.map((reason) => (
                  <div key={reason.title} className="flex items-start gap-4 group p-4 rounded-xl hover:bg-white transition-colors border border-transparent hover:border-gray-100">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-sage/15 flex-shrink-0 mt-0.5 group-hover:bg-sage/25 transition-colors">
                      <i className={`${reason.icon} text-sage text-lg`}></i>
                    </div>
                    <div>
                      <p className="font-bold text-navy text-sm mb-1 leading-tight">{reason.title}</p>
                      <p className="text-gray-500 text-xs leading-relaxed">{reason.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right image collage */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-2xl overflow-hidden h-60 w-full">
                  <img src={IMAGES[0]} alt="Senior exercise with caregiver" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="rounded-2xl overflow-hidden h-44 w-full mt-10">
                  <img src={IMAGES[1]} alt="Senior with caregiver laughing" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="rounded-2xl overflow-hidden h-44 w-full -mt-5">
                  <img src={IMAGES[2]} alt="Reading with senior" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="rounded-2xl overflow-hidden h-60 w-full mt-4">
                  <img src={IMAGES[3]} alt="Care planning at home" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
                </div>
              </div>
              {/* Floating badge */}
              <div
                className="absolute bottom-5 left-5 bg-navy text-white rounded-2xl px-6 py-4"
                style={{ boxShadow: '0 8px 30px rgba(15,42,68,0.35)' }}
              >
                <div className="flex items-center gap-0.5 mb-1.5">
                  {[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-gold text-sm"></i>)}
                </div>
                <p className="text-white font-bold text-sm leading-tight">5-Star Rated by Families</p>
                <p className="text-white/45 text-xs mt-0.5">Oakland, IL &amp; East-Central Illinois</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Caregiver Credibility Section */}
      <section className="py-20 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-block w-10 h-px bg-gold"></span>
                <p className="text-gold text-xs font-bold uppercase tracking-[0.18em]">Caregiver Excellence</p>
              </div>
              <h2 className="font-heading text-navy font-bold mb-5 leading-tight" style={{ fontSize: 'clamp(1.6rem, 2.8vw, 2.6rem)' }}>
                Our Rigorous Caregiver<br />Screening Process
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-7 max-w-md">
                Every Oak Senior Care caregiver undergoes a thorough multi-step vetting process before ever entering your home. Your loved one deserves only the most trustworthy, skilled, and compassionate professionals.
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                {[
                  { icon: 'ri-shield-check-fill', label: 'Licensed & Insured', bg: 'bg-sage/15', color: 'text-sage' },
                  { icon: 'ri-fingerprint-line', label: 'Background Checked', bg: 'bg-sage/15', color: 'text-sage' },
                  { icon: 'ri-graduation-cap-line', label: 'Certified Trained', bg: 'bg-gold/15', color: 'text-gold' },
                ].map((badge) => (
                  <div key={badge.label} className={`flex items-center gap-2 ${badge.bg} px-4 py-2 rounded-full`}>
                    <i className={`${badge.icon} ${badge.color} text-sm`}></i>
                    <span className="text-navy text-xs font-bold">{badge.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {SCREENING_STEPS.map((step) => (
                <div
                  key={step.title}
                  className="bg-offWhite rounded-2xl p-6 border border-gray-100 hover:border-sage transition-colors group"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-navy/8 mb-4 group-hover:bg-navy/12 transition-colors">
                    <i className={`${step.icon} text-navy text-lg`}></i>
                  </div>
                  <p className="font-bold text-navy text-sm mb-1.5 leading-tight">{step.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
